import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pedidoApi, recebimentoApi } from '../services/api';
import { Modal, Loading, EmptyState, Badge, BadgeSituacao, BadgePagamento } from './ui';
import { formatarMoeda, formatarData, formatarTelefone } from '../lib/format';
import { HandCoins, Search, RotateCcw, AlertCircle } from 'lucide-react';

const formasPagamento = [
  { value: '', label: 'Todas as formas' },
  { value: 'DINHEIRO', label: 'Dinheiro' },
  { value: 'PIX', label: 'PIX' },
  { value: 'CARTAO_DEBITO', label: 'Cartão Débito' },
  { value: 'CARTAO_CREDITO', label: 'Cartão Crédito' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'TRANSFERENCIA', label: 'Transferência' },
];

const situacoesPedido = [
  { value: '', label: 'Todas as situações' },
  { value: 'AGUARDANDO_ENTRADA', label: 'Aguardando Entrada' },
  { value: 'EM_PRODUCAO', label: 'Em Produção' },
  { value: 'PRONTO', label: 'Pronto' },
  { value: 'ENTREGUE', label: 'Entregue' },
];

const situacoesPagamento = [
  { value: '', label: 'Pendente + Parcial' },
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'PARCIAL', label: 'Parcial' },
];

const origens = [
  { value: '', label: 'Todas as origens' },
  { value: 'PAINEL', label: 'Painel' },
  { value: 'LOJA', label: 'Loja Virtual' },
];

const tiposPrazo = [
  { value: '', label: 'Todos os prazos' },
  { value: 'URGENTE', label: 'Urgente' },
  { value: 'PRAZO_CURTO', label: 'Prazo Curto' },
  { value: 'PRAZO_CONFORTAVEL', label: 'Confortável' },
];

const filtrosVazios = {
  busca: '',
  situacao: '',
  situacaoPagamento: '',
  formaPagamento: '',
  origem: '',
  tipoPrazo: '',
  inicio: '',
  fim: '',
  valorMin: '',
  valorMax: '',
};

/* ══ Modal: lista de pedidos com débito + filtros combináveis ══ */
export function ModalDebitos({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const [filtros, setFiltros] = useState({ ...filtrosVazios });
  const [pedidoBaixa, setPedidoBaixa] = useState<any>(null);

  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== ''),
  );

  const { data, isLoading } = useQuery({
    queryKey: ['pedidos-debitos', params],
    queryFn: () => pedidoApi.debitos(params).then(r => r.data),
    enabled: aberto,
  });

  const set = (k: string, v: string) => setFiltros(f => ({ ...f, [k]: v }));
  const filtrosAtivos = Object.values(filtros).filter(v => v !== '').length;

  return (
    <>
      <Modal
        aberto={aberto}
        onFechar={onFechar}
        titulo="Pedidos com Débitos Pendentes"
        descricao={`${data?.total ?? 0} pedido(s) · ${formatarMoeda(data?.valorEmAberto)} em aberto`}
        largura="max-w-5xl"
      >
        {/* Filtros combináveis */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              value={filtros.busca}
              onChange={e => set('busca', e.target.value)}
              placeholder="Cliente ou telefone..."
              className="input-field !py-2 !pl-9 text-sm"
            />
          </div>
          <select value={filtros.situacao} onChange={e => set('situacao', e.target.value)} className="input-field !py-2 text-sm">
            {situacoesPedido.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filtros.situacaoPagamento} onChange={e => set('situacaoPagamento', e.target.value)} className="input-field !py-2 text-sm">
            {situacoesPagamento.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filtros.formaPagamento} onChange={e => set('formaPagamento', e.target.value)} className="input-field !py-2 text-sm">
            {formasPagamento.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select value={filtros.origem} onChange={e => set('origem', e.target.value)} className="input-field !py-2 text-sm">
            {origens.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filtros.tipoPrazo} onChange={e => set('tipoPrazo', e.target.value)} className="input-field !py-2 text-sm">
            {tiposPrazo.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="flex gap-2">
            <input type="date" value={filtros.inicio} onChange={e => set('inicio', e.target.value)} className="input-field !py-2 text-sm" title="Pedidos a partir de" />
            <input type="date" value={filtros.fim} onChange={e => set('fim', e.target.value)} className="input-field !py-2 text-sm" title="Pedidos até" />
          </div>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2 items-center">
            <input
              type="number" min="0" step="0.01" placeholder="Valor mín."
              value={filtros.valorMin} onChange={e => set('valorMin', e.target.value)}
              className="input-field !py-1.5 text-sm w-28"
            />
            <span className="text-ink-300 text-xs">até</span>
            <input
              type="number" min="0" step="0.01" placeholder="Valor máx."
              value={filtros.valorMax} onChange={e => set('valorMax', e.target.value)}
              className="input-field !py-1.5 text-sm w-28"
            />
          </div>
          {filtrosAtivos > 0 && (
            <button
              onClick={() => setFiltros({ ...filtrosVazios })}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-400 hover:text-red-600"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Limpar {filtrosAtivos} filtro(s)
            </button>
          )}
        </div>

        {/* Lista */}
        {isLoading ? (
          <Loading />
        ) : (data?.data || []).length === 0 ? (
          <EmptyState
            icon={HandCoins}
            titulo="Nenhum débito encontrado"
            descricao="Ajuste os filtros ou todos os pedidos estão quitados."
          />
        ) : (
          <div className="space-y-2.5">
            {data.data.map((p: any) => (
              <div
                key={p.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-white ring-1 ring-inset ring-ink-200/70"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">
                    #{p.numero} · {p.cliente?.nome}
                  </p>
                  <p className="text-xs text-ink-400 mt-0.5">
                    {formatarTelefone(p.cliente?.telefone)} · pedido em {formatarData(p.dataPedido)}
                  </p>
                  <div className="flex gap-1.5 mt-2">
                    <BadgeSituacao situacao={p.situacao} />
                    <BadgePagamento situacao={p.situacaoPagamento} />
                    {p.origem === 'LOJA' && <Badge tom="brand" label="Loja" />}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-ink-400">
                    Total {formatarMoeda(p.valorTotal)}
                    {p.valorRecebido > 0 && ` · recebido ${formatarMoeda(p.valorRecebido)}`}
                  </p>
                  <p className="font-display text-lg font-bold text-red-600">
                    {formatarMoeda(p.saldoDevedor)}
                  </p>
                </div>
                <button
                  onClick={() => setPedidoBaixa(p)}
                  className="btn-primary !py-2 text-[13px] shrink-0"
                >
                  <HandCoins className="w-4 h-4" /> Baixa
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {pedidoBaixa && (
        <ModalBaixa pedido={pedidoBaixa} onFechar={() => setPedidoBaixa(null)} />
      )}
    </>
  );
}

/* ══ Modal: registrar baixa (parcial ou total) de um pedido ══ */
export function ModalBaixa({ pedido, onFechar }: { pedido: any; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [valor, setValor] = useState(String(pedido.saldoDevedor ?? ''));
  const [formaPagamento, setFormaPagamento] = useState(pedido.formaPagamento || '');
  const [dataRecebimento, setDataRecebimento] = useState(() => new Date().toISOString().split('T')[0]);
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState('');

  const criar = useMutation({
    mutationFn: (dados: any) => recebimentoApi.criar(dados),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedidos-debitos'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['projecao-caixa'] });
      onFechar();
    },
    onError: (err: any) => {
      setErro(err.response?.data?.error || 'Não foi possível registrar a baixa.');
    },
  });

  const saldo = Number(pedido.saldoDevedor ?? pedido.valorTotal);
  const valorNum = parseFloat(valor) || 0;
  const parcial = valorNum > 0 && valorNum < saldo - 0.009;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    if (valorNum <= 0) { setErro('Informe um valor maior que zero.'); return; }
    if (valorNum > saldo + 0.01) { setErro(`Valor maior que o saldo devedor (${formatarMoeda(saldo)}).`); return; }
    criar.mutate({
      pedidoId: pedido.id,
      valor: valorNum,
      formaPagamento: formaPagamento || undefined,
      data: new Date(`${dataRecebimento}T12:00:00`).toISOString(),
      observacoes: observacoes.trim() || undefined,
    });
  };

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={`Baixa — Pedido #${pedido.numero}`}
      descricao={`${pedido.cliente?.nome || ''} · saldo devedor ${formatarMoeda(saldo)}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="input-label">Valor Recebido *</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink-400">R$</span>
            <input
              type="number" step="0.01" min="0.01" max={saldo}
              value={valor} onChange={e => setValor(e.target.value)}
              className="input-field !pl-10" required autoFocus
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className={`flex items-center gap-1.5 text-2xs font-medium ${parcial ? 'text-blue-600' : 'text-ink-400'}`}>
              {parcial && <AlertCircle className="w-3 h-3" />}
              {parcial
                ? `Baixa parcial — restarão ${formatarMoeda(saldo - valorNum)}`
                : 'Quitando o valor total do pedido'}
            </p>
            {valorNum !== saldo && (
              <button type="button" onClick={() => setValor(String(saldo))} className="text-2xs font-semibold text-brand-600 hover:text-brand-700">
                Usar saldo total
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="input-label">Forma de Pagamento</label>
            <select value={formaPagamento} onChange={e => setFormaPagamento(e.target.value)} className="input-field">
              {formasPagamento.map(f => (
                <option key={f.value} value={f.value}>{f.value === '' ? 'Selecionar...' : f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="input-label">Data do Recebimento</label>
            <input type="date" value={dataRecebimento} onChange={e => setDataRecebimento(e.target.value)} className="input-field" />
          </div>
        </div>
        <div>
          <label className="input-label">Observações</label>
          <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2} className="input-field" placeholder="Opcional" />
        </div>
        {erro && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700">{erro}</div>
        )}
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={criar.isPending} className="btn bg-emerald-600 text-white shadow-sm hover:bg-emerald-700">
            {criar.isPending ? 'Registrando...' : parcial ? 'Registrar Baixa Parcial' : 'Quitar Pedido'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
